import { publishDraftMasterMenuProduct } from "@/api/admin.js";

export function emptyProductDraft() {
  return {
    upc_code: "",
    title: "",
    subtitle: "",
    description: "",
    company: "",
    volume_label: "",
    sku: "",
    abv: "",
    image_small: "",
    image_large: "",
    additional_images: [],
    default_category: "",
    categories: [],
    disabled: false,
    no_tax: false,
    avalara_tax_code: "",
  };
}

export function normalizeAvailableCategories(data) {
  const raw = Array.isArray(data?.categories) ? data.categories : [];
  return raw
    .map((c) => {
      const id = String(c?.id || c?._id || "").trim();
      const name = String(c?.name || c?.label || "").trim();
      const rowType = String(c?.type || "").trim();
      if (!id || !name || rowType === "divider") return null;
      const tag = String(c?.tag || "").trim();
      return { id, name, tag, is_alcohol: Boolean(c?.is_alcohol) };
    })
    .filter(Boolean);
}

export function draftRecordToFormDraft(record) {
  const primarySmall = String(record?.image_small || record?.image || record?.image_large || "").trim();
  const primaryLarge = String(record?.image_large || record?.image || primarySmall || "").trim();
  const categoryIds = Array.isArray(record?.category_ids)
    ? record.category_ids.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  const defaultId = String(record?.default_category_id || "").trim();
  const otherCategories = categoryIds.filter((id) => id && id !== defaultId);

  return {
    ...emptyProductDraft(),
    upc_code: String(record?.upc_code || "").trim(),
    title: String(record?.title || "").trim(),
    subtitle: String(record?.subtitle || "").trim(),
    description: String(record?.description || "").trim(),
    company: String(record?.company || "").trim(),
    volume_label: String(record?.volume_label || "").trim(),
    sku: String(record?.sku || "").trim(),
    abv: String(record?.abv || "").trim(),
    image_small: primarySmall || primaryLarge,
    image_large: primaryLarge || primarySmall,
    additional_images: Array.isArray(record?.additional_images)
      ? record.additional_images.map((url) => String(url || "").trim()).filter(Boolean)
      : [],
    default_category: defaultId || String(record?.type || "").trim(),
    categories: otherCategories,
    disabled: Boolean(record?.disabled),
    no_tax: Boolean(record?.no_tax),
    avalara_tax_code: String(record?.avalara_tax_code || "").trim(),
  };
}

function resolveCategoryId(input, options) {
  const value = String(input || "").trim();
  if (!value) return "";
  const byId = options.find((c) => c.id === value);
  if (byId) return byId.id;
  const needle = value.toLowerCase();
  const byName = options.find((c) => c.name.toLowerCase() === needle);
  return byName ? byName.id : "";
}

function categoryTagForId(categoryId, options) {
  const id = String(categoryId || "").trim();
  if (!id) return "";
  const row = options.find((c) => c.id === id);
  return row && row.tag ? String(row.tag).trim() : "";
}

function isUnmatchedCategoryOption(cat) {
  if (!cat) return false;
  const name = String(cat.name || "")
    .trim()
    .toUpperCase();
  const tag = String(cat.tag || "")
    .trim()
    .toUpperCase();
  return name === "UNMATCHED" || tag === "NO_MATCH" || tag === "XXX";
}

/** Autocreated drafts with no real category (UNMATCHED / NO_MATCH) cannot bulk publish. */
export function isDraftUnmatchedCategory(record, availableCategories = []) {
  const typeRaw = String(record?.type || "").trim();
  const typeLower = typeRaw.toLowerCase();
  if (typeLower === "unmatched" || typeLower === "no_match") {
    return true;
  }

  const defaultId = String(record?.default_category_id || "").trim();
  const draft = draftRecordToFormDraft(record);
  const defaultInput = String(draft.default_category || "").trim();

  const candidates = [typeRaw, defaultInput, defaultId].filter(Boolean);
  for (const input of candidates) {
    const lower = input.toLowerCase();
    if (lower === "unmatched" || lower === "no_match") {
      return true;
    }
    const byId = availableCategories.find((c) => c.id === input);
    if (isUnmatchedCategoryOption(byId)) return true;
    const byName = availableCategories.find((c) => c.name.toLowerCase() === lower);
    if (isUnmatchedCategoryOption(byName)) return true;
    const byTag = availableCategories.find((c) => c.tag === input);
    if (isUnmatchedCategoryOption(byTag)) return true;
  }
  return false;
}

export function splitDraftsForBulkPublish(rows, availableCategories = []) {
  const publishable = [];
  const unmatched = [];
  for (const row of rows) {
    if (isDraftUnmatchedCategory(row, availableCategories)) {
      unmatched.push(row);
    } else {
      publishable.push(row);
    }
  }
  return { publishable, unmatched };
}

function alcoholCategorySelected(draft, availableCategories) {
  const cats = Array.isArray(draft.categories) ? draft.categories : [];
  const ids = new Set(
    [String(draft.default_category || "").trim(), ...cats.map((c) => String(c || "").trim())].filter(
      Boolean
    )
  );
  for (const id of ids) {
    const resolved = resolveCategoryId(id, availableCategories) || id;
    if (availableCategories.find((c) => c.id === resolved)?.is_alcohol) return true;
  }
  return false;
}

function optionalManufacturerSku(draft, draftSkuId = "") {
  const sku = String(draft?.sku || "").trim();
  const draftSku = String(draftSkuId || "").trim();
  if (!sku) return undefined;
  if (draftSku && sku === draftSku) return undefined;
  if (/^DRAFT_/i.test(sku)) return undefined;
  return sku;
}

export function validateMasterMenuPublishDraft(draft, availableCategories) {
  const upc = String(draft?.upc_code || "").trim();
  if (!upc) return "UPC required.";
  if (!String(draft?.title || "").trim()) return "Name required.";

  const defaultInput = String(draft?.default_category || "").trim();
  if (!defaultInput) return "Default category is required.";

  const defaultId = resolveCategoryId(defaultInput, availableCategories) || defaultInput;
  const typeTag = categoryTagForId(defaultId, availableCategories);
  if (!typeTag) {
    return "Default category must have a tag. Try reloading categories or pick another default.";
  }
  return null;
}

export function buildMasterMenuPublishPayload(draft, availableCategories, draftSkuId = "") {
  const defaultInput = String(draft?.default_category || "").trim();
  const defaultId = resolveCategoryId(defaultInput, availableCategories) || defaultInput;
  const typeTag = categoryTagForId(defaultId, availableCategories);

  const otherCategoryIds = (Array.isArray(draft.categories) ? draft.categories : [])
    .map((id) => resolveCategoryId(id, availableCategories) || String(id || "").trim())
    .filter(Boolean)
    .filter((id) => id !== defaultId);
  const category_tags = Array.from(
    new Set(
      otherCategoryIds.map((id) => categoryTagForId(id, availableCategories)).filter(Boolean)
    )
  );

  const primaryLarge = String(draft.image_large || draft.image_small || "").trim();
  const primarySmall = String(draft.image_small || draft.image_large || "").trim();

  const payload = {
    upc_code: String(draft.upc_code || "").trim(),
    title: String(draft.title || "").trim(),
    subtitle: String(draft.subtitle || "").trim(),
    description: String(draft.description || "").trim(),
    company: String(draft.company || "").trim(),
    volume: String(draft.volume_label || "").trim(),
    image_small: primarySmall || undefined,
    image_large: primaryLarge || undefined,
    ...(Array.isArray(draft.additional_images) && draft.additional_images.length
      ? {
          additional_images: draft.additional_images
            .map((url) => String(url || "").trim())
            .filter(Boolean),
        }
      : {}),
    type: typeTag,
    ...(category_tags.length ? { category_tags } : {}),
    disabled: Boolean(draft.disabled),
    no_tax: Boolean(draft.no_tax),
    avalara_tax_code: String(draft.avalara_tax_code || "").trim() || undefined,
  };

  const mpnSku = optionalManufacturerSku(draft, draftSkuId);
  if (mpnSku) {
    payload.sku = mpnSku;
  }

  if (alcoholCategorySelected(draft, availableCategories)) {
    const abvRaw = String(draft.abv || "").trim();
    if (abvRaw) {
      const n = parseFloat(abvRaw.replace(/%$/, "").trim());
      payload.abv = Number.isFinite(n) ? n : abvRaw;
    }
  }

  return payload;
}

export async function publishMasterMenuDraft({ draftId, draft, availableCategories, draftSkuId = "" }) {
  const validationError = validateMasterMenuPublishDraft(draft, availableCategories);
  if (validationError) {
    throw new Error(validationError);
  }

  const payload = buildMasterMenuPublishPayload(draft, availableCategories, draftSkuId);
  if (!draftId) {
    throw new Error("Draft id is required to publish.");
  }

  try {
    const res = await publishDraftMasterMenuProduct(draftId, payload);
    return res?.data ?? null;
  } catch (e) {
    const created = e?.response?.data?.data;
    if (created?.sku_id) {
      const msg = e?.response?.data?.message || e?.message || "Draft cleanup failed";
      const err = new Error(msg);
      err.createdProduct = created;
      throw err;
    }
    throw e;
  }
}
